import { resolveDateRangeBound } from '~/utils/date'
import type { FilterDefinition, FiltersState } from './types'

export const isFilterActive = (
  def: FilterDefinition,
  filters: FiltersState
): boolean => {
  if (def.type === 'dateRange') {
    return (
      (def.keyAfter in filters && filters[def.keyAfter] !== '') ||
      (def.keyBefore in filters && filters[def.keyBefore] !== '')
    )
  }
  return def.key in filters && filters[def.key] !== ''
}

export const definitionKey = (def: FilterDefinition): string =>
  def.type === 'dateRange' ? `${def.keyAfter}|${def.keyBefore}` : def.key

/** Active filters in the order of `Object.keys(filters)` (matches URL / navigate merge order). */
export const getActiveDefinitionsInKeyOrder = (
  filters: FiltersState,
  definitions: FilterDefinition[]
): FilterDefinition[] => {
  const seen = new Set<string>()
  const out: FilterDefinition[] = []
  for (const key of Object.keys(filters)) {
    const def = definitions.find((d) =>
      d.type === 'dateRange'
        ? d.keyAfter === key || d.keyBefore === key
        : d.key === key
    )
    if (!def || !isFilterActive(def, filters)) continue
    const id = definitionKey(def)
    if (!seen.has(id)) {
      seen.add(id)
      out.push(def)
    }
  }
  for (const def of definitions) {
    const id = definitionKey(def)
    if (isFilterActive(def, filters) && !seen.has(id)) {
      seen.add(id)
      out.push(def)
    }
  }
  return out
}

export const sanitizeFilters = (filters: FiltersState): FiltersState =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '')
  )

export const filtersToQueryString = (filters: FiltersState): string => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params.set(key, value)
    }
  }
  params.sort()
  return params.toString()
}

export const queryStringToFilters = (qs: string): FiltersState => {
  const params = new URLSearchParams(qs)
  const filters: FiltersState = {}
  for (const [key, value] of params.entries()) {
    filters[key] = value
  }
  return filters
}

export const normalizeQueryString = (queryString: string): string =>
  filtersToQueryString(queryStringToFilters(queryString))

/**
 * Resolves raw filter values into the values expected by the API, based on the
 * filter definitions. The raw value stays in the URL / saved search; only the
 * object passed to the API request is resolved.
 *
 * Resolution is dispatched per definition type, so new behaviors (not
 * necessarily date-related) can be added here later.
 */
export const resolveFilters = <
  T extends Record<string, string | string[] | number | undefined>
>(
  filters: T,
  definitions: FilterDefinition[]
): T => {
  const overrides: Record<string, string> = {}
  for (const def of definitions) {
    switch (def.type) {
      case 'dateRange': {
        const after = filters[def.keyAfter]
        if (typeof after === 'string') {
          overrides[def.keyAfter] = resolveDateRangeBound(after, 'after')
        }
        const before = filters[def.keyBefore]
        if (typeof before === 'string') {
          overrides[def.keyBefore] = resolveDateRangeBound(before, 'before')
        }
        break
      }
    }
  }
  return { ...filters, ...overrides }
}

export const toFiltersState = (
  filters: Record<string, string | number | undefined>
): FiltersState =>
  Object.fromEntries(
    Object.entries(filters)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  )
